import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../../../../components/hooks/useAuth';

import {
  getLatestPolicies,
  getLatestQuotes,
} from '../../_services/dashboard.service';
import type { CoverageView } from './coverage.types';
import { buildCoverageView } from './coverageView';

const EMPTY: CoverageView = {
  groups: [],
  hero: undefined,
  others: [],
  attention: [],
  hasAnyCoverage: false,
};

/**
 * Both endpoints answer with a single object rather than a list, so today an
 * account with two policies can only ever reach us as one.
 *
 * That is FIR-21110 phase 1 and the one item with an external dependency: the
 * list reads (`/api/policy/v1/search`, `/api/quote/v1/:accountId`) exist but
 * have not been confirmed in scope for a policyholder actor. Everything below
 * this line already works on lists, so swapping the fetch is the whole change
 * when they are -- nothing downstream assumes a single line.
 */
const toList = (payload: unknown): Record<string, any>[] => {
  if (Array.isArray(payload)) {
    return payload.filter(Boolean);
  }

  if (
    payload &&
    typeof payload === 'object' &&
    Object.keys(payload).length > 0
  ) {
    return [payload as Record<string, any>];
  }

  return [];
};

export interface UseCoverageViewResult {
  readonly view: CoverageView;
  readonly isLoading: boolean;
  /** The read failed. Distinct from an account that genuinely has no coverage. */
  readonly isError: boolean;
}

/**
 * The account's coverage, resolved once.
 *
 * A failed read is reported rather than smoothed into an empty view: telling a
 * policyholder they have no cover because a request timed out is the worst
 * thing this tile could say.
 */
export function useCoverageView(accountId?: string): UseCoverageViewResult {
  // Keyed by the account on the TOKEN, not the one in redux. These reads resolve
  // their account from the JWT, so the cache must be keyed by the same thing the
  // server scopes by. Keyed on a redux value that hydrates later, the first result
  // lands under `undefined`, is reused across account switches, and then the key
  // changes underneath it and the tile drops back to a skeleton.
  const { tokenAccountId } = useAuth();

  const query = useQuery(
    ['dashboard-coverage', tokenAccountId ?? accountId],
    () =>
      // Settled, not all: a failed quotes read must not hide a policy that loaded. Only a
      // total failure is an error -- otherwise one flaky endpoint tells an insured holding
      // cover that we could not load any of it.
      Promise.allSettled([getLatestPolicies(), getLatestQuotes()]).then(
        ([policies, quotes]) => {
          // The policies read IS the coverage. Substituting an empty list for a failed one
          // and carrying on reports an insured as uncovered because a request failed --
          // the very thing this hook's error state exists to prevent.
          if (policies.status === 'rejected') {
            throw policies.reason;
          }

          // Quotes are additive. Losing them costs the reader a pending decision, which is
          // worth far less than hiding the cover they hold.
          return {
            policies: toList(policies.value?.data),
            quotes:
              quotes.status === 'fulfilled' ? toList(quotes.value?.data) : [],
          };
        }
      ),
    // Deliberately NOT gated on accountId. Both reads take no account -- they resolve one
    // from the JWT -- so gating the fetch on a redux value suppressed the request entirely
    // whenever account details had not landed yet, and the tile then reported an insured
    // with cover as having none. accountId stays in the key so a switch cannot serve the
    // previous account's coverage from cache.
    { refetchOnWindowFocus: false }
  );

  const view = useMemo(() => {
    if (!query.data) {
      return EMPTY;
    }

    return buildCoverageView(query.data);
  }, [query.data]);

  return {
    view,
    isLoading: query.isInitialLoading,
    isError: query.isError,
  };
}

/**
 * Exported for the quick-action rail, which reads the same two endpoints and needs the same
 * tolerance for their shape.
 */
export { toList as readCoverageList };

export default useCoverageView;
